import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';

import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { showToast } from '../../../helpers';
import { NAVIGATE_PATH as PATH } from '../../../helpers/constants';
import {
  apiMessage,
  apiStatus,
  getMyTeachingClass,
  markMyTeachingAttendance,
  submitMyTravelPrefs,
  TRosterStatus,
  TTeachingRunSheet,
  TTeachingTravel,
} from '../../../requests/opsInstructors';
import {
  btnPrimary,
  btnSecondary,
  Card,
  EmptyState,
  inputClass,
  labelClass,
  PageBody,
  PageHeader,
  Pill,
  tableClass,
  tdClass,
  theadClass,
  thClass,
} from '../../Admin/ui';
import { fmtWhen } from '../MyClasses';

/**
 * Travel (T4.04): the instructor answers the travel trigger here instead of
 * by email — home airport, airline, timing, or "no travel needed". Finance
 * gets the note; the class keeps the answer and its travel status moves.
 */
const TravelCard: React.FC<{ classId: string; travel: TTeachingTravel }> = ({
  classId,
  travel,
}) => {
  const queryClient = useQueryClient();
  const [none, setNone] = useState(false);
  const [airport, setAirport] = useState(travel.prefs?.homeAirport ?? '');
  const [airline, setAirline] = useState(travel.prefs?.airline ?? '');
  const [timing, setTiming] = useState(travel.prefs?.timing ?? '');
  const [notes, setNotes] = useState('');
  const submit = useMutation({
    mutationFn: () =>
      submitMyTravelPrefs(classId, {
        noneRequired: none,
        ...(none
          ? {}
          : {
              homeAirport: airport.trim() || undefined,
              airline: airline.trim() || undefined,
              timing: timing.trim() || undefined,
            }),
        notes: notes.trim() || undefined,
      }),
    onSuccess: (r) => {
      showToast(
        `${none ? 'Noted — no travel needed' : 'Sent to finance'}${
          r.financeCcMissing ? ' (finance address not set; cx@ has it)' : ''
        } · travel status ${r.travel.status ?? '—'}`,
        'success',
      );
      setNotes('');
      queryClient.invalidateQueries({ queryKey: ['teaching', classId] });
    },
    onError: (err) => showToast(apiMessage(err), 'error'),
  });
  const prev = travel.prefs;
  const settled = travel.status === 'Booked' || travel.status === 'Invoiced';
  const busy = submit.isPending;
  const canSend =
    !busy && (none || airport.trim() || airline.trim() || timing.trim());
  return (
    <Card
      title="Travel"
      right={
        travel.status ? (
          <Pill value={travel.status} tone={settled ? 'good' : 'info'} />
        ) : (
          <span className="text-xs text-gray-bold">Onsite class</span>
        )
      }
    >
      {prev && (
        <div className="border-b border-white-three px-5 py-3 text-sm text-gray-five">
          {prev.noneRequired ? (
            <>You said no travel is needed</>
          ) : (
            <>
              On file: {prev.homeAirport ?? '—'} · {prev.airline ?? '—'}
              {prev.timing ? ` · ${prev.timing}` : ''}
            </>
          )}
          <span className="ml-2 text-xs text-gray-bold">
            {new Date(prev.submittedAt).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
            })}
          </span>
        </div>
      )}
      {settled ? (
        <div className="px-5 py-4 text-sm text-gray-bold">
          Finance has {travel.status === 'Booked' ? 'booked' : 'invoiced'} this
          trip. Anything to change goes to them directly.
        </div>
      ) : (
        <form
          className="grid gap-3 px-5 py-4 md:grid-cols-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (canSend) submit.mutate();
          }}
        >
          <label className="flex items-center gap-2 text-sm md:col-span-3">
            <input
              type="checkbox"
              checked={none}
              onChange={(e) => setNone(e.target.checked)}
              disabled={busy}
            />
            No travel needed for this class
          </label>
          <div>
            <label className={labelClass}>Home airport</label>
            <input
              className={`${inputClass} w-full`}
              placeholder="SFO"
              maxLength={120}
              value={airport}
              onChange={(e) => setAirport(e.target.value)}
              disabled={busy || none}
            />
          </div>
          <div>
            <label className={labelClass}>Airline</label>
            <input
              className={`${inputClass} w-full`}
              placeholder="United, Alaska…"
              maxLength={120}
              value={airline}
              onChange={(e) => setAirline(e.target.value)}
              disabled={busy || none}
            />
          </div>
          <div>
            <label className={labelClass}>Timing</label>
            <input
              className={`${inputClass} w-full`}
              placeholder="Fly in the evening before, out after class"
              maxLength={400}
              value={timing}
              onChange={(e) => setTiming(e.target.value)}
              disabled={busy || none}
            />
          </div>
          <div className="md:col-span-3">
            <label className={labelClass}>Notes (optional)</label>
            <input
              className={`${inputClass} w-full`}
              placeholder="Hotel near the site, rental car, anything finance should know"
              maxLength={2000}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={busy}
            />
          </div>
          <div className="flex items-center gap-3 md:col-span-3">
            <button type="submit" className={btnPrimary} disabled={!canSend}>
              {busy
                ? 'Sending…'
                : none
                ? 'Confirm no travel'
                : 'Send to finance'}
            </button>
            <span className="text-xs text-gray-bold">
              Goes to finance with the scheduling team copied; the class keeps
              your answer.
            </span>
          </div>
        </form>
      )}
    </Card>
  );
};

/** Seats an instructor can mark; anything else (cancelled, waiting list…) is the office's. */
const MARKABLE = ['Booked', 'Attended', 'No Attend', 'Completed'];

const linkClass =
  'rounded-md border border-white-three px-2.5 py-1 text-xs text-blue-primary transition-colors hover:border-blue-500';

const Count: React.FC<{ label: string; value: number }> = ({
  label,
  value,
}) => (
  <div>
    <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-bold">
      {label}
    </div>
    <div className="text-xl font-bold text-gray-four [font-variant-numeric:tabular-nums]">
      {value}
    </div>
  </div>
);

/**
 * One class, everything the instructor needs on the day: the links to open
 * before the first learner arrives, the notes, the roster, and attendance.
 * Learners are name, email and status — nothing more leaves the API.
 */
const RunSheet: React.FC = () => {
  const { id = '' } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['teaching', id],
    queryFn: () => getMyTeachingClass(id),
    enabled: Boolean(id),
    retry: false,
  });

  const mark = useMutation({
    mutationFn: (vars: { registrationId: string; status: TRosterStatus }) =>
      markMyTeachingAttendance(id, vars),
    onSuccess: (res) => {
      // Show the new status straight away, then let the server confirm it.
      queryClient.setQueryData<TTeachingRunSheet>(['teaching', id], (prev) =>
        prev
          ? {
              ...prev,
              roster: prev.roster.map((r) =>
                r.id === res.registrationId ? { ...r, status: res.status } : r,
              ),
            }
          : prev,
      );
      queryClient.invalidateQueries({ queryKey: ['teaching'] });
    },
    onError: (err) => showToast(apiMessage(err), 'error'),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <LoadingSpinner />
      </div>
    );
  }
  if (error || !data) {
    return (
      <>
        <PageHeader
          title="Class"
          back={{ to: PATH.teaching, label: 'Your classes' }}
        />
        <PageBody>
          <Card>
            <EmptyState
              title={
                apiStatus(error) === 403
                  ? 'That class is not on your schedule'
                  : 'Could not load this class'
              }
              hint={error ? apiMessage(error) : undefined}
            />
          </Card>
        </PageBody>
      </>
    );
  }

  const { class: cls, roster, counts, instructorSurvey, travel } = data;
  const surveyLink = instructorSurvey?.link ?? cls.links?.instructorSurvey;
  const started = cls.start
    ? new Date(cls.start).getTime() <= Date.now()
    : false;
  const links = cls.links;
  const program = cls.notes?.program;
  const trainerNotes = cls.notes?.trainer;
  const hasLinks =
    Boolean(cls.zoomRoom?.joinUrl) ||
    Boolean(links?.learnerLink) ||
    Boolean(links?.privateClassLink) ||
    Boolean(links?.courseOutline) ||
    Boolean(links?.icalLink);
  const hasNotes =
    Boolean(program?.visible && program.info) || Boolean(trainerNotes);
  const busyId = mark.isPending ? mark.variables?.registrationId : undefined;

  return (
    <>
      <PageHeader
        title={cls.name ?? '(unnamed class)'}
        back={{ to: PATH.teaching, label: 'Your classes' }}
        meta={
          <span className="flex flex-wrap items-center gap-2">
            <span>{fmtWhen(cls.start, cls.end)}</span>
            {cls.accountName && <span>· {cls.accountName}</span>}
            {cls.classType && <span>· {cls.classType}</span>}
            <Pill value={cls.status} />
          </span>
        }
      />
      <PageBody>
        <div className="grid gap-5">
          <Card title="Before you start">
            {!hasLinks && !hasNotes ? (
              <div className="px-5 py-4 text-sm text-gray-bold">
                No links or notes on this class yet.
              </div>
            ) : (
              <>
                {hasLinks && (
                  <div className="flex flex-wrap items-center gap-2 px-5 py-4 text-sm">
                    {cls.zoomRoom?.joinUrl && (
                      <a
                        className="inline-flex items-center gap-1.5 rounded-md bg-blue-primary px-3.5 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-one"
                        href={cls.zoomRoom.joinUrl}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Join {cls.zoomRoom.name ?? 'Zoom'} ↗
                      </a>
                    )}
                    {(
                      [
                        ['learnerLink', 'Learner link'],
                        ['privateClassLink', 'Private class link'],
                        ['courseOutline', 'Course outline'],
                        ['icalLink', 'Calendar file'],
                      ] as const
                    ).map(([key, label]) =>
                      links?.[key] ? (
                        <a
                          key={key}
                          className={linkClass}
                          href={links[key] as string}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {label} ↗
                        </a>
                      ) : null,
                    )}
                  </div>
                )}
                {hasNotes && (
                  <div
                    className={`grid gap-4 px-5 py-4 text-sm md:grid-cols-2 ${
                      hasLinks ? 'border-t border-white-three' : ''
                    }`}
                  >
                    {program?.visible && program.info && (
                      <div className="md:col-span-2">
                        <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-bold">
                          From the program
                          {program.programName
                            ? ` · ${program.programName}`
                            : ''}
                        </div>
                        <div className="whitespace-pre-wrap text-gray-five">
                          {program.info}
                        </div>
                      </div>
                    )}
                    {trainerNotes && (
                      <div className="md:col-span-2">
                        <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-bold">
                          Notes for you
                        </div>
                        <div className="whitespace-pre-wrap text-gray-five">
                          {trainerNotes}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </Card>

          {travel?.applies && <TravelCard classId={id} travel={travel} />}

          {(surveyLink || instructorSurvey?.filed) && (
            <Card title="After class">
              <div className="flex flex-wrap items-center gap-3 px-5 py-4 text-sm">
                {surveyLink ? (
                  <a
                    className={linkClass}
                    href={surveyLink}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Instructor survey ↗
                  </a>
                ) : (
                  <span className="text-gray-bold">
                    No instructor survey link on this class yet.
                  </span>
                )}
                {instructorSurvey?.filed ? (
                  <Pill value="Filed" tone="good" />
                ) : (
                  <span className="text-xs text-gray-bold">
                    Not filed yet — takes about two minutes once the class is
                    done.
                  </span>
                )}
              </div>
            </Card>
          )}

          <Card
            title="Registrations"
            right={
              !started ? (
                <span className="text-xs text-gray-bold">
                  Attendance opens when the class starts
                </span>
              ) : undefined
            }
          >
            <div className="flex flex-wrap gap-8 border-b border-white-three px-5 py-4">
              <Count label="Booked" value={counts.booked} />
              <Count label="Attended" value={counts.attended} />
              <Count label="No attend" value={counts.noAttend} />
            </div>
            {roster.length === 0 ? (
              <EmptyState
                title="No registrations yet"
                hint="Seats booked on this class will show here."
              />
            ) : (
              <div className="overflow-x-auto">
                <table className={tableClass}>
                  <thead className={theadClass}>
                    <tr>
                      <th className={thClass}>Name</th>
                      <th className={thClass}>Email</th>
                      <th className={thClass}>Status</th>
                      <th className={`${thClass} text-right`}>Attendance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {roster.map((r) => {
                      const markable = MARKABLE.includes(String(r.status));
                      const busy = busyId === r.id;
                      return (
                        <tr key={r.id}>
                          <td
                            className={`${tdClass} font-medium text-gray-four`}
                          >
                            {r.name ?? '—'}
                          </td>
                          <td className={`${tdClass} text-gray-three`}>
                            {r.email ?? '—'}
                          </td>
                          <td className={tdClass}>
                            <Pill value={r.status} />
                          </td>
                          <td className={`${tdClass} text-right`}>
                            {markable ? (
                              <span className="inline-flex gap-1.5">
                                <button
                                  type="button"
                                  className={`${btnSecondary} px-2.5 py-1 text-xs`}
                                  disabled={
                                    !started ||
                                    busy ||
                                    r.status === 'Attended' ||
                                    r.status === 'Completed'
                                  }
                                  onClick={() =>
                                    mark.mutate({
                                      registrationId: r.id,
                                      status: 'Attended',
                                    })
                                  }
                                >
                                  Attended
                                </button>
                                <button
                                  type="button"
                                  className={`${btnSecondary} px-2.5 py-1 text-xs`}
                                  disabled={
                                    !started || busy || r.status === 'No Attend'
                                  }
                                  onClick={() =>
                                    mark.mutate({
                                      registrationId: r.id,
                                      status: 'No Attend',
                                    })
                                  }
                                >
                                  No attend
                                </button>
                              </span>
                            ) : (
                              <span className="text-xs text-gray-two">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {!started && roster.length > 0 && (
              <div className="border-t border-white-three px-5 py-3 text-xs text-gray-bold">
                Attendance opens when the class starts.
              </div>
            )}
          </Card>
        </div>
      </PageBody>
    </>
  );
};

export default RunSheet;
